import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RXJS
import { Observable, throwError, Subscription, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// ENVIRONEMNTS
import { environment } from '@app/env-service';

import {
  BusinessAccountContactInterface,
  UserAccountInterface,
} from './../../../modules/clients/interfaces/business-contacts.interface';

@Injectable({
  providedIn: 'root',
})
export class BusinessAccountContactsService {
  private readonly _contacts = new BehaviorSubject<
    BusinessAccountContactInterface[]
  >(<BusinessAccountContactInterface[]>[]);
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _filteredContacts = new BehaviorSubject<
    BusinessAccountContactInterface[]
  >(<BusinessAccountContactInterface[]>[]);

  contacts$ = this._contacts.asObservable();
  filteredContacts$ = this._filteredContacts.asObservable();
  loading$ = this._loading.asObservable();

  url :string = environment.urlV2 + '/b2b-portal/business-account-contacts';

  constructor(private http :HttpClient) {}

  get(params ?:{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.http
      .get(`${this.url}?relation[]=userAccount`, { params })
      .subscribe(
        (response :any) => {
          this._contacts.next(response.data);
          this._loading.next(false);
        },
        (response :any) => throwError(response.error),
      );
  }

  create(payload :any) :Observable<any> {
    return this.http.post(`${this.url}`, payload).pipe(
      map((response :BusinessAccountContactInterface) => {
        return response;
      }),
      catchError((response) => {
        return throwError(response.error);
      }),
    );
  }

  createBulk(contact :any) :Observable<any> {
    return this.http.post(`${this.url}/multiple`, contact).pipe(
      map((response :any) => {
        return response;
      }),
      catchError((response) => {
        return throwError(response.error);
      }),
    );
  }

  delete(id :number) :Observable<any> {
    return this.http.delete(`${this.url}/${id}`).pipe(
      map((response :any) => {
        return response;
      }),
      catchError((response) => {
        return throwError(response.error);
      }),
    );
  }

  setPrimaryContact(params :{ id :number; primary :boolean }) :Observable<any> {
    const { id, primary } = params;

    return this.http.put(`${this.url}/${id}/primary`, { primary }).pipe(
      map((response :any) => {
        return response;
      }),
      catchError((response) => {
        return throwError(response.error);
      }),
    );
  }

  unsetPrimaryContactForBusiness(businessAccountId :number) :Observable<any> {
    return this.http
      .post(`${this.url}/unsetPrimaryContactForBusiness`, { businessAccountId })
      .pipe(
        map((response :any) => {
          return response;
        }),
        catchError((response) => {
          return throwError(response.error);
        }),
      );
  }

  removeFromAccount(id :number) :Observable<any> {
    return this.http.delete(`${this.url}/${id}`).pipe(
      map((response :any) => {
        return response;
      }),
      catchError((response) => {
        return throwError(response.error);
      }),
    );
  }

  getFilteredContacts({ key, value }) :UserAccountInterface[] {
    const contacts = this._contacts.value;

    return contacts.filter((c) => c[key] === value).map((c) => c.userAccount);
  }

  getByBusinessAccountId(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    this._loading.next(true);

    return this.http.get(`${this.url}?relation[]=userAccount`, { params }).pipe(
      map((response :BusinessAccountContactInterface) => {
        return response;
      }),
      catchError((response) => {
        return throwError(response.error);
      }),
    );
  }
}
